import { memo, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setRealtimeLayer } from "../model/actions";
import tralisAPI from "../TralisAPI";
import TralisLayer from "../TralisLayer";
import isVehicleToHighlight from "../utils/isVehicleToHighlight";

function RealtimeLayer() {
  const config = useSelector((state) => state.config);
  const hideOthers = useSelector((state) => state.hideOthers);
  const train = useSelector((state) => state.train);
  const trainAuto = useSelector((state) => state.trainAuto);
  const debug = useSelector((state) => state.debug);
  const dispatch = useDispatch();
  const map = useSelector((state) => state.map);
  const mode = useSelector((state) => state.mode);
  const selectedVehicles = useSelector((state) => state.selectedVehicles);

  // Define the filter function for the tracker layer.
  const filter = useMemo(() => {
    let filterr = config.filterVehicles;
    if (train && !trainAuto && hideOthers) {
      filterr = (trajectory) => {
        const vehicle = trajectory.properties;
        return isVehicleToHighlight(vehicle, train);
      };
    }

    return filterr;
  }, [config.filterVehicles, hideOthers, train, trainAuto]);

  const layer = useMemo(() => {
    return new TralisLayer({
      api: tralisAPI,
      config,
      debug,
      filter,
      mode,
      sort: config.sortVehicles,
      tenant: config.tenant,
    });
  }, [config, debug, filter, mode]);

  useEffect(() => {
    const onOpen = () => {
      // [SBAHNMW-750]
      // Before opening a connection we remove all trajectories that have
      // a time_intervals in the paste, it will
      // avoid phantom train that are at the end of their route but we never
      // receive the deleted_vehicle event because we have changed the browser tab.
      Object.entries(layer?.trajectories).forEach(([key, trajectory]) => {
        const timeIntervals = trajectory?.properties?.time_intervals;
        if (timeIntervals.length) {
          const lastTimeInterval = timeIntervals[timeIntervals.length - 1][0];
          if (lastTimeInterval < Date.now()) {
            layer.engine.removeTrajectory(key);
          }
        }
      });
    };
    window.addEventListener("tralisapi:open", onOpen);
    dispatch(setRealtimeLayer(layer));

    return () => {
      window.removeEventListener("tralisapi:open", onOpen);
    };
  }, [dispatch, layer]);

  useEffect(() => {
    if (layer) {
      map?.addLayer(layer);

      // For testing the realtime canvas in Cypress
      map?.once("postrender", () => {
        layer.container?.setAttribute("data-testid", "tracker-layer-container");
      });
    }
    return () => {
      if (layer) {
        map?.removeLayer(layer);
      }
    };
  }, [layer, map]);

  useEffect(() => {
    if (layer) {
      // eslint-disable-next-line
      layer.selectedVehicleId =
        selectedVehicles?.length === 1 ? selectedVehicles[0].train_id : null;
    }
    return () => {
      if (layer) {
        layer.selectedVehicleId = null;
      }
    };
  }, [layer, selectedVehicles]);

  return null;
}

export default memo(RealtimeLayer);
